import { useCallback, useState } from 'react';
import { FaWifi, FaSearch, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaList, FaMapMarkerAlt, FaKeyboard } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import AddressAutocomplete from './shared/AddressAutocomplete';
import NbnAvailabilityResult from './shared/NbnAvailabilityResult';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];
const STREET_TYPES = ['ST', 'RD', 'AVE', 'PL', 'CT', 'DR', 'CRES', 'BLVD', 'PDE', 'LN', 'WAY', 'HWY', 'TCE', 'CL', 'CCT', 'GR', 'ESP', 'SQ'];

const PRESETS = [
  { label: '43 Overall Ave, Casey ACT', v: { streetNumber: '43', streetName: 'OVERALL', streetType: 'AVE', suburb: 'CASEY', state: 'ACT', postcode: '2913' } },
  { label: '1 Martin Pl, Sydney NSW', v: { streetNumber: '1', streetName: 'MARTIN', streetType: 'PL', suburb: 'SYDNEY', state: 'NSW', postcode: '2000' } },
];

const VENDOR_KEYS = ['status', 'responseType', 'transactionId', 'dirTransactionId', 'directoryId', 'nbnLocationId', 'technology', 'accessTechnology', 'attainableSpeed', 'csa', 'qualifyPostcode', 'locationReference', 'addressLong', 'message', 'errorCode', 'faultString'];

const EMPTY_FORM = {
  streetNumber: '', streetName: '', streetType: 'ST',
  suburb: '', state: 'NSW', postcode: '',
  unitNumber: '', unitType: '',
};

// Reformat a Vocus premise label to lead with unit/suite + level, e.g.
//   "SUITE 50 101 COLLINS STREET MELBOURNE VIC  (LEVEL 27)"
// → "SUITE 50, LEVEL 27 · 101 COLLINS STREET MELBOURNE VIC".
const formatPremiseLabel = (raw, streetNumber) => {
  let s = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  let level = '';
  const lvl = s.match(/\s*\(([^)]*)\)\s*$/);
  if (lvl && typeof lvl.index === 'number') {
    level = lvl[1].trim();
    s = s.slice(0, lvl.index).trim();
  }
  let unit = '';
  let street = s;
  const sn = String(streetNumber || '').trim();
  if (sn) {
    const idx = s.lastIndexOf(' ' + sn + ' ');
    if (idx > 0) {
      unit = s.slice(0, idx).trim();
      street = s.slice(idx + 1).trim();
    }
  }
  const lead = [unit, level].filter(Boolean).join(', ');
  return lead ? `${lead} · ${street}` : street;
};

const authHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    // QUALIFY + SEARCH are "public" modes, so the lambda only attaches the full
    // qualifyDetail (NTD table, copper pairs, service class, CVC ID) when it can
    // verify an admin. Without this header we get the sanitised customer payload
    // and the vendor-portal view below stays empty.
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export default function NbnAvailabilityCheck() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [addressText, setAddressText] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // ── premise auto-search (DIR::Get BROADBAND) state ──
  const [searching, setSearching] = useState(false);
  const [premises, setPremises] = useState(null);   // null = not searched, [] = searched/empty
  const [searchError, setSearchError] = useState(null);
  const [activeDirectoryId, setActiveDirectoryId] = useState(null);
  // The DIR record we qualified. Its LOC *and* its address text come from the same
  // Vocus BroadbandAddressRecord, so showing both together is the proof that the
  // qualification landed on the premise the staff member clicked. FIBRE::Get QUALIFY
  // does not echo an address back, so without this the Address row is blank.
  const [activePremise, setActivePremise] = useState(null);

  // ── Service status by Service ID (FIBRE::Get SERVICE) ──
  const [svcId, setSvcId] = useState('');
  const [svcLoading, setSvcLoading] = useState(false);
  const [svcResult, setSvcResult] = useState(null);
  const [svcError, setSvcError] = useState(null);

  const checkServiceStatus = async (e) => {
    e?.preventDefault?.();
    setSvcError(null);
    setSvcResult(null);
    const id = svcId.trim();
    if (!id) { setSvcError('Enter a Vocus Service ID.'); return; }
    setSvcLoading(true);
    try {
      // GET_SERVICE_DETAILS is an OPERATIONAL Vocus mode → the lambda requires a
      // verified admin (security gate, 2026-07-14). Send the admin Bearer token.
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        headers: authHeaders(),
        // The lambda has an early guard requiring addressComponents (QUALIFY-centric); the
        // service-by-id modes don't use it, so we pass a dummy to get past the guard — same
        // workaround the backend's own checkVocusStatus uses.
        body: JSON.stringify({ mode: 'GET_SERVICE_DETAILS', serviceId: id, addressComponents: { postcode: '0000' } }),
      });
      const data = await res.json().catch(() => ({}));
      setSvcResult(data);
      if (!res.ok) setSvcError(data?.message || data?.details || data?.error || `HTTP ${res.status}`);
    } catch (err) {
      setSvcError(err.message || 'Network error');
    } finally {
      setSvcLoading(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const applyPreset = (p) => { setForm((f) => ({ ...f, ...p })); setManualOpen(true); setAddressText(''); };

  const buildComponents = useCallback((src) => {
    const f = src || form;
    return {
      streetNumber: String(f.streetNumber || '').trim().toUpperCase(),
      streetName: String(f.streetName || '').trim().toUpperCase(),
      streetType: String(f.streetType || '').trim().toUpperCase(),
      suburb: String(f.suburb || '').trim().toUpperCase(),
      city: String(f.suburb || '').trim().toUpperCase(),
      state: f.state,
      postcode: String(f.postcode || '').trim(),
      unitNumber: String(f.unitNumber || '').trim().toUpperCase(),
      unitType: String(f.unitType || '').trim().toUpperCase(),
    };
  }, [form]);

  // Run a QUALIFY. When directoryId is supplied (from a picked premise) the
  // qualification is pinned to that exact LOC instead of a street-level guess.
  const runCheck = async (e, directoryId = null, srcForm = null) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);
    setActiveDirectoryId(directoryId);
    if (!directoryId) setActivePremise(null);

    const f = srcForm || form;
    if (!directoryId) {
      if (!String(f.streetName).trim() || !String(f.streetType).trim() || !String(f.suburb).trim() || !f.state || !String(f.postcode).trim()) {
        setError('Street name, street type, suburb, state and postcode are required.');
        return;
      }
      if (!/^\d{4}$/.test(String(f.postcode).trim())) {
        setError('Postcode must be a 4-digit Australian postcode.');
        return;
      }
    }

    setLoading(true);
    try {
      const addressComponents = buildComponents(f);
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ mode: 'QUALIFY', addressComponents, ...(directoryId ? { directoryId } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      setResult(data);
      if (!res.ok) {
        setError(data?.message || data?.details || data?.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  // Auto-search every NBN premise at a street address (units / suites / levels)
  // via DIR::Get BROADBAND. Postcode is NOT required — search is broadest with
  // just street name + suburb + state.
  const runSearch = async (e, srcForm = null, { autoQualifySingle = false } = {}) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);
    setSearchError(null);
    setPremises(null);
    setActiveDirectoryId(null);
    setActivePremise(null);

    const f = srcForm || form;
    if (!String(f.streetName).trim() || !String(f.streetType).trim() || !String(f.suburb).trim() || !f.state) {
      setSearchError('Street name, street type, suburb and state are required to search.');
      return;
    }

    setSearching(true);
    try {
      // Search the BUILDING — drop the unit so Vocus returns the FULL premise
      // roster (every unit/suite/level), not a single narrowed best-match.
      const searchComponents = { ...buildComponents(f), unitNumber: '', unitType: '' };
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ mode: 'SEARCH_NBN_ADDRESS', addressComponents: searchComponents }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSearchError(data?.message || data?.details || data?.error || `HTTP ${res.status}`);
        return;
      }
      const list = Array.isArray(data?.premises) ? data.premises : [];
      setPremises(list);
      if (list.length === 0) {
        setSearchError('No NBN premises matched. Try widening the search — drop the unit and street number.');
      } else if (autoQualifySingle && list.length === 1 && list[0].directoryId) {
        // A house rather than a building: one LOC, so skip the picker entirely.
        // Go through the same path as a click so the result still carries the
        // premise's own Vocus address text.
        setActivePremise(list[0]);
        void runCheck(null, list[0].directoryId, f);
      }
    } catch (err) {
      setSearchError(err?.message || String(err));
    } finally {
      setSearching(false);
    }
  };

  /**
   * Google Places gave us a Vocus-normalised address — fill the manual fields
   * (so staff can see and tweak what we're about to send) and immediately look
   * up every NBN premise at that address, the same path the public site takes.
   */
  const onAddressSelected = ({ components, formattedAddress }) => {
    const next = {
      streetNumber: components.streetNumber || '',
      streetName: components.streetName || '',
      streetType: components.streetType || 'ST',
      suburb: components.suburb || '',
      state: AU_STATES.includes(components.state) ? components.state : (components.state || 'NSW'),
      postcode: components.postcode || '',
      unitNumber: components.unitNumber || '',
      unitType: components.unitType || '',
    };
    setForm(next);
    setAddressText(formattedAddress);
    void runSearch(null, next, { autoQualifySingle: true });
  };

  const resetAll = () => {
    setForm(EMPTY_FORM);
    setAddressText('');
    setResult(null);
    setError(null);
    setPremises(null);
    setSearchError(null);
    setActiveDirectoryId(null);
    setActivePremise(null);
  };

  const qualifyPremise = (p) => {
    if (!p?.directoryId) return;
    setActivePremise(p);
    runCheck(null, p.directoryId);
  };

  const isPass = result?.status === 'PASS';
  const isFail = result && (result.status === 'FAIL' || result.status === 'REJECTED');
  const isErr = result?.status === 'ERROR' || (!isPass && !isFail && error);
  const detail = result?.qualifyDetail || null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <FaWifi className="text-2xl text-[#fabd2f]" />
        <h1 className="text-2xl font-bold text-[#ebdbb2]">NBN Availability Check</h1>
      </div>
      <p className="text-sm text-[#a89984] mb-6">
        Staff-only tool — runs a live <code className="text-[#fabd2f]">DIR::Get BROADBAND</code> address lookup and <code className="text-[#fabd2f]">FIBRE QUALIFY</code> against Vocus production.
      </p>

      {/* Service status by Service ID — live FIBRE::Get SERVICE */}
      <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <FaSearch className="text-[#83a598]" />
          <h2 className="text-lg font-semibold text-[#ebdbb2]">Check Service Status</h2>
        </div>
        <p className="text-xs text-[#a89984] mb-3">
          Live <code className="text-[#fabd2f]">FIBRE::Get SERVICE</code> by Vocus Service ID — e.g. for an order submitted via the Vocus portal.
        </p>
        <form onSubmit={checkServiceStatus} className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-[#928374] mb-1">Vocus Service ID</label>
            <input
              type="text"
              value={svcId}
              onChange={(e) => setSvcId(e.target.value)}
              placeholder="e.g. 13822916"
              className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded-lg text-[#ebdbb2] placeholder-[#665c54] text-sm focus:outline-none focus:border-[#83a598]"
            />
          </div>
          <button
            type="submit"
            disabled={svcLoading}
            className="px-5 py-2 rounded-lg font-medium bg-[#83a598] text-[#1d2021] hover:bg-[#689d6a] transition-colors disabled:opacity-60"
          >
            {svcLoading ? 'Checking…' : 'Check Status'}
          </button>
        </form>

        {svcError && <p className="text-sm text-[#fb4934] mt-3">{svcError}</p>}

        {svcResult && (() => {
          const p = svcResult.params || svcResult || {};
          const status = p.ServiceStatus || svcResult.status || '—';
          const statusColor = status === 'ACTIVE' ? 'text-[#b8bb26]'
            : status === 'PROCESSING' ? 'text-[#fabd2f]'
            : (status === 'INACTIVE' || status === 'FAILED' || status === 'ERROR') ? 'text-[#fb4934]'
            : 'text-[#ebdbb2]';
          const rows = [
            ['Customer', p.CustomerName],
            ['Phone', p.Phone],
            ['Username', p.Username],
            ['Order Type', p.OrderType],
            ['Service Type', p.ServiceType],
            ['Plan ID', p.PlanID],
            ['Service Level', p.ServiceLevel],
            ['LOC / CSA', [p.DirectoryID, p.CSA].filter(Boolean).join(' · ')],
            ['IP Assignment', p.IPAssignment],
          ].filter(([, v]) => v != null && v !== '');
          return (
            <div className="mt-4 bg-[#1d2021] border border-[#3c3836] rounded-lg p-4">
              <p className="text-sm mb-3">Status: <span className={`font-bold ${statusColor}`}>{status}</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-[#3c3836]/50 py-1">
                    <span className="text-[#928374]">{k}</span>
                    <span className="text-[#ebdbb2] text-right">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Address search ── */}
      <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FaMapMarkerAlt className="text-[#fabd2f]" />
            <h2 className="text-lg font-semibold text-[#ebdbb2]">Search an address</h2>
          </div>
          <p className="text-xs text-[#a89984] mb-3">
            Same Google address lookup the public site uses. Picking a result lists every NBN premise
            (unit / suite / level) at that building — choose one to qualify its exact LOC.
          </p>
          <AddressAutocomplete
            value={addressText}
            onChange={setAddressText}
            onSelect={onAddressSelected}
            disabled={loading || searching}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => setManualOpen((o) => !o)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] border border-[#504945]"
          >
            <FaKeyboard /> {manualOpen ? 'Hide manual entry' : 'Enter address manually'}
          </button>
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p.v)}
              className="px-3 py-1.5 text-xs rounded-lg bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] border border-[#504945]"
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={resetAll}
            className="px-3 py-1.5 text-xs rounded-lg bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] border border-[#504945] ml-auto"
          >
            Reset
          </button>
        </div>

        {manualOpen && (
          <form onSubmit={(e) => runSearch(e, null, { autoQualifySingle: true })} className="space-y-3 pt-2 border-t border-[#3c3836]">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Unit (optional)" v={form.unitNumber} on={(v) => update('unitNumber', v)} />
              <Field label="Street number" v={form.streetNumber} on={(v) => update('streetNumber', v)} placeholder="43" />
              <Field label="Street name *" v={form.streetName} on={(v) => update('streetName', v)} placeholder="OVERALL" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select label="Street type *" v={form.streetType} on={(v) => update('streetType', v)} options={STREET_TYPES} />
              <Field label="Suburb *" v={form.suburb} on={(v) => update('suburb', v)} placeholder="CASEY" />
              <Select label="State *" v={form.state} on={(v) => update('state', v)} options={AU_STATES} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Postcode *" v={form.postcode} on={(v) => update('postcode', v.replace(/\D/g, '').slice(0, 4))} placeholder="2913" />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={loading || searching}
                title="Lists every NBN premise at the address, then qualifies it automatically if there is only one"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] font-semibold disabled:opacity-50"
              >
                <FaList />
                {searching ? 'Searching…' : loading ? 'Qualifying…' : 'Check Availability'}
              </button>
              <button
                type="button"
                onClick={(e) => runCheck(e)}
                disabled={loading || searching}
                title="Skip the premise roster and qualify the address directly. Vocus collapses a multi-premise building to a single record, so on a unit block this qualifies its LAST match — not necessarily the customer's unit."
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984] text-sm disabled:opacity-50"
              >
                <FaSearch />
                Qualify address directly
              </button>
            </div>
          </form>
        )}

        {(searching || loading) && (
          <p className="text-xs text-[#fabd2f]">
            {searching ? 'Looking up NBN premises at this address…' : 'Qualifying with Vocus…'}
          </p>
        )}
      </div>

      {searchError && (
        <div className="mt-5 p-4 rounded-lg bg-amber-900/40 border border-amber-700 text-amber-200 text-sm flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5" /> {searchError}
        </div>
      )}

      {Array.isArray(premises) && premises.length > 0 && (
        <div className="mt-6 bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
          <div className="px-4 py-2 flex items-center justify-between bg-[#1d2021] border-b border-[#3c3836]">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[#a89984]">
              NBN premises found ({premises.length})
            </h2>
            <span className="text-[11px] text-[#a89984]">Click a premise to qualify that exact LOC</span>
          </div>
          {premises.length > 1 && !activeDirectoryId && (
            <p className="px-4 py-2 text-[11px] text-[#d79921] bg-[#d79921]/10 border-b border-[#3c3836]">
              Pick the exact premise. Qualifying the street address instead collapses these {premises.length} records
              to one on the Vocus side — you&apos;d get the last of them, which is rarely the customer&apos;s unit.
            </p>
          )}
          <div className="max-h-96 overflow-y-auto divide-y divide-[#3c3836]">
            {premises.map((p) => {
              const active = activeDirectoryId === p.directoryId;
              return (
                <button
                  key={p.directoryId}
                  type="button"
                  onClick={() => qualifyPremise(p)}
                  disabled={loading}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition disabled:opacity-50 ${active ? 'bg-[#fabd2f]/15' : 'hover:bg-[#3c3836]'}`}
                >
                  <FaMapMarkerAlt className={`mt-0.5 shrink-0 ${active ? 'text-[#fabd2f]' : 'text-[#83a598]'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#ebdbb2] truncate">{p.addressLong ? formatPremiseLabel(p.addressLong, form.streetNumber) : '(no address text)'}</div>
                    <div className="text-[11px] text-[#a89984] font-mono mt-0.5">
                      {p.directoryId}
                      {p.carrier ? ` · ${p.carrier}` : ''}
                      {p.locationReference ? ` · ${p.locationReference}` : ''}
                    </div>
                  </div>
                  {loading && active
                    ? <FaSearch className="mt-0.5 shrink-0 text-[#fabd2f] animate-pulse" />
                    : <span className="text-[11px] text-[#a89984] mt-0.5 shrink-0">Qualify →</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && !result && (
        <div className="mt-5 p-4 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5" /> {error}
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-4">
          {/* The vendor-portal availability page — admin payload only. */}
          {detail ? (
            <NbnAvailabilityResult
              detail={detail}
              result={result}
              locationId={activePremise?.directoryId || activeDirectoryId || result.nbnLocationId || result.directoryId}
              addressLong={activePremise?.addressLong || result.addressLong}
            />
          ) : (
            <>
              <ResultBanner pass={isPass} fail={isFail} err={isErr} result={result} />
              {isPass && (
                <div className="p-4 rounded-lg bg-amber-900/30 border border-amber-700/60 text-amber-200 text-sm flex items-start gap-2">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>
                    Vocus returned a PASS but no availability detail — the lambda fell back to the public payload
                    because it couldn&apos;t verify the admin token. Log out and back in to see the full page.
                  </span>
                </div>
              )}
            </>
          )}

          <details className="bg-[#282828] border border-[#3c3836] rounded-xl">
            <summary className="px-4 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-[#a89984] bg-[#1d2021] border-b border-[#3c3836]">
              Vendor response (parsed)
            </summary>
            <table className="w-full text-sm">
              <tbody>
                {VENDOR_KEYS.filter((k) => result[k] !== null && result[k] !== undefined && result[k] !== '').map((k, i) => (
                  <tr key={k} className={i % 2 === 1 ? 'bg-[#1d2021]/40' : ''}>
                    <td className="px-4 py-2 text-[#a89984] font-medium w-44 align-top">{k}</td>
                    <td className="px-4 py-2 text-[#ebdbb2] font-mono text-xs break-all">{String(result[k])}</td>
                  </tr>
                ))}
                {Array.isArray(result.availablePlans) && result.availablePlans.length > 0 && (
                  <tr className="bg-[#1d2021]/40">
                    <td className="px-4 py-2 text-[#a89984] font-medium align-top">availablePlans</td>
                    <td className="px-4 py-2 text-[#ebdbb2] font-mono text-xs">{result.availablePlans.join(', ')} Mbps</td>
                  </tr>
                )}
              </tbody>
            </table>
          </details>

          {result.params && Object.keys(result.params).length > 0 && (
            <details className="bg-[#282828] border border-[#3c3836] rounded-xl">
              <summary className="px-4 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-[#a89984] bg-[#1d2021] border-b border-[#3c3836]">
                Vocus Param map ({Object.keys(result.params).length})
              </summary>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.keys(result.params).sort().map((k, i) => (
                      <tr key={k} className={i % 2 === 1 ? 'bg-[#1d2021]/40' : ''}>
                        <td className="px-4 py-2 text-[#fabd2f] font-medium w-56 align-top text-xs">{k}</td>
                        <td className="px-4 py-2 text-[#ebdbb2] font-mono text-xs break-all">{String(result.params[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <details className="bg-[#282828] border border-[#3c3836] rounded-xl">
            <summary className="px-4 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-[#a89984] bg-[#1d2021] border-b border-[#3c3836]">
              Raw JSON
            </summary>
            <pre className="p-4 text-[11px] text-[#ebdbb2] overflow-x-auto max-h-96">{JSON.stringify(result, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#a89984] mb-1">{label}</span>
      <input
        type="text"
        value={v}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
      />
    </label>
  );
}

function Select({ label, v, on, options }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#a89984] mb-1">{label}</span>
      <select
        value={v}
        onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function ResultBanner({ pass, fail, err, result }) {
  if (pass) {
    return (
      <div className="p-4 rounded-xl bg-emerald-900/30 border border-emerald-700/60">
        <div className="flex items-center gap-2 mb-2">
          <FaCheckCircle className="text-emerald-400 text-xl" />
          <h3 className="text-lg font-semibold text-emerald-200">PASS — service available</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat k="Technology" v={result.accessTechnology || result.technology} />
          <Stat k="LOC" v={result.nbnLocationId} />
          <Stat k="CSA" v={result.csa} />
          <Stat k="Speeds" v={Array.isArray(result.availablePlans) ? result.availablePlans.join('/') + ' Mbps' : '—'} />
        </div>
      </div>
    );
  }
  if (fail) {
    return (
      <div className="p-4 rounded-xl bg-amber-900/30 border border-amber-700/60">
        <div className="flex items-center gap-2 mb-1">
          <FaExclamationTriangle className="text-amber-400 text-xl" />
          <h3 className="text-lg font-semibold text-amber-200">{result.status} — not serviceable</h3>
        </div>
        <p className="text-amber-100/80 text-sm">
          Vocus found this address ({result.addressLong || '—'}) but reports no NBN service available. LOC: <span className="font-mono">{result.nbnLocationId || '—'}</span>
        </p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-4 rounded-xl bg-red-900/30 border border-red-700/60">
        <div className="flex items-center gap-2 mb-1">
          <FaTimesCircle className="text-red-400 text-xl" />
          <h3 className="text-lg font-semibold text-red-200">Error</h3>
        </div>
        <p className="text-red-100/80 text-sm break-words">{result.message || result.details || result.error || 'Unknown error.'}</p>
      </div>
    );
  }
  return null;
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-xs text-[#a89984]">{k}</div>
      <div className="text-sm font-semibold text-[#ebdbb2] font-mono break-all">{v ?? '—'}</div>
    </div>
  );
}
